import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, TextField, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import { RequestProps, User } from '../../store/types'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/system'
import { useDebouncedScroll } from '../../store/utils/useDebouncedScroll'
import { getIsLeader, getIsManagement, getIsProjectLeader } from '../../store/redux/reducers/currentUserSlice'
import { useSelector } from 'react-redux'

interface UserSelectorProps {
  selectedUser: User | null
  setSelectedUser: (user: User | null) => void
  currentUser: User | null
  currentView: string
  employeeStatus?: string
  extraUsers?: User[]
}

const UserSelector = ({
  selectedUser,
  setSelectedUser,
  currentUser,
  currentView,
  employeeStatus,
  extraUsers,
}: UserSelectorProps) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [displayedUsers, setDisplayedUsers] = useState<Map<string, User>>(new Map())
  const [previousValue, setPreviousValue] = useState<User | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const navigate = useNavigate()
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const isManagement = useSelector(getIsManagement)
  const isLeader = useSelector(getIsLeader) && !isManagement
  const isProjectLeader = useSelector(getIsProjectLeader) && !isManagement

  const userProps = useMemo(() => {
    const defaultProps: RequestProps = {
      page: page,
      size: 20,
      'leaders.id': isLeader && !isProjectLeader ? currentUser?.id : undefined,
      'projects.leaders.leader.id': isProjectLeader && !isLeader ? currentUser?.id : undefined,
      'query:projects.leaders.leader.id,leaders.id': isLeader && isProjectLeader ? currentUser?.id : undefined,
      'query:firstName,lastName': searchTerm,
      employeeStatus: employeeStatus,
    }
    return defaultProps
  }, [page, currentUser, isLeader, searchTerm, isProjectLeader, employeeStatus])

  const { data: usersData } = useGetLightUsersQuery(userProps, {
    skip: !currentUser,
  })

  useEffect(() => {
    if (usersData) {
      setDisplayedUsers(prev => {
        const newUsers = usersData.content.filter(user => !prev.has(user.id))
        const updatedMap = new Map(prev)
        newUsers.forEach(user => updatedMap.set(user.id, user))
        return updatedMap
      })
    }
  }, [usersData, page, searchTerm])

  const handleFetchMoreData = useCallback(() => {
    if (!usersData?.last) {
      setPage(prev => prev + 1)
    }
  }, [usersData])

  const onSelectBottomScroll = useDebouncedScroll(handleFetchMoreData)

  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      const newSearchTerm = event.target.value
      setSearchTerm(newSearchTerm)
      setPage(0)
      if (newSearchTerm === '') {
        setDisplayedUsers(new Map())
      }
    }, 500)
  }, [])

  const userList = Array.from(displayedUsers.values())

  const mergedOptions = useMemo(() => {
    const base = currentUser && !displayedUsers.has(currentUser.id) ? [...userList, currentUser] : userList
    const combined = !extraUsers?.length
      ? base
      : [...base, ...extraUsers.filter(u => !displayedUsers.has(u.id) && u.id !== currentUser?.id)]
    return combined.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
  }, [userList, currentUser, displayedUsers, extraUsers])

  return (
    <Box sx={{ height: 30, width: '100%', maxWidth: isDownSm ? 'none' : 350 }}>
      <Autocomplete
        sx={{ mb: 2, width: '100%', height: 30 }}
        value={selectedUser}
        onChange={(_, value) => {
          if (value && value !== selectedUser && value !== previousValue) {
            setPreviousValue(value)
            setSelectedUser(value)
            navigate(`${currentView}/${value.id}`)
          }
        }}
        getOptionLabel={user => `${user?.firstName} ${user?.lastName}`}
        options={mergedOptions}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {`${option.firstName} ${option.lastName}`}
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={({ InputLabelProps, ...params }) => (
          <TextField
            onChange={handleSearchInputChange}
            {...params}
            label={t('page.worklog.input.selectUser')}
            size="small"
          />
        )}
        ListboxProps={{
          style: { maxHeight: 200 },
          onScroll: onSelectBottomScroll,
        }}
      />
    </Box>
  )
}

export default UserSelector
